/**
 * Audio cue dispatcher.
 *
 * Backend ships ``scene_hint.audio_cue`` on ~80% of envelopes
 * (see ``PodPlatform/src/.../scene_mapper.py``).  The frontend
 * historically ignored every one of them -- this module fixes that.
 *
 * Sound is **synthesised** with the Web Audio API rather than
 * shipped as MP3 / OGG files: no asset pipeline, no autoplay
 * permission failures on cold start, no bandwidth.  Each cue is a
 * short envelope-shaped oscillator (or noise burst) with a distinct
 * pitch / waveform so the operator can learn the vocabulary by ear.
 *
 * Activation is opt-in: the AudioContext is lazily created on first
 * non-muted cue, which means we satisfy the browser's user-gesture
 * autoplay policy as long as the user toggles audio on via a click.
 *
 * Cue vocabulary (matches ``scene_mapper.py`` and
 * ``FRONTEND_COVERAGE_BACKLOG.md``):
 *
 *     bubble_pop             agent speech / order submitted
 *     thinking_chime         agent.thinking_started
 *     order_ping             order.submitted
 *     order_fill_cha_ching   order.filled (full fill)
 *     risk_warn_beep         risk.state_changed → watch; session_warning
 *     frozen_door_slam       risk.state_changed → frozen
 *     unfreeze_thaw          risk.state_changed → recovery; crowd_relieved
 *     reduce_haircut_swoosh  risk.state_changed → reduced
 *     utility_calc_tick      utility.recorded
 *     leaderboard_climb      leaderboard.rank_changed
 *     its_friday_baby        celebration.weekly_started  (60 s loop)
 *     confetti_burst         celebration.podium rank=1
 *     applause               celebration.podium rank=2,3
 *     morning_chime          building.session_opened
 *     closing_bell           building.session_closed
 *     weather_*_ambient      building.weather_changed (long loop)
 */

import type { AnyEnvelope } from '@/events';

export type AudioCue =
  | 'bubble_pop'
  | 'thinking_chime'
  | 'order_ping'
  | 'order_fill_cha_ching'
  | 'risk_warn_beep'
  | 'frozen_door_slam'
  | 'unfreeze_thaw'
  | 'reduce_haircut_swoosh'
  | 'utility_calc_tick'
  | 'leaderboard_climb'
  | 'its_friday_baby'
  | 'confetti_burst'
  | 'applause'
  | 'morning_chime'
  | 'closing_bell'
  | 'weather_sunny_ambient'
  | 'weather_cloudy_ambient'
  | 'weather_storm_ambient'
  | 'weather_windy_ambient'
  | 'weather_snow_ambient'
  | 'weather_fog_ambient';

/** Rate-limit window so a 10-event burst doesn't fire 10 chimes. */
const COOLDOWN_MS_PER_CUE: Partial<Record<AudioCue, number>> = {
  bubble_pop: 250,
  thinking_chime: 400,
  order_ping: 120,
  utility_calc_tick: 600,
  risk_warn_beep: 1_500,
};

export class AudioCueDispatcher {
  private ctx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private muted = true;
  private lastFiredAt = new Map<string, number>();
  /** Long-running ambient loop sources (one per kind). */
  private ambient: { source: OscillatorNode; gain: GainNode; kind: string } | null = null;
  private lastEnvelopeId: string | null = null;

  setMuted(muted: boolean): void {
    this.muted = muted;
    if (muted) {
      this.stopAmbient();
    }
  }

  /**
   * Apply the dispatcher to the latest envelope on the feed.  Idempotent:
   * the dispatcher remembers the last envelope id it processed and skips
   * repeats so it can be hooked into every store subscribe.
   */
  ingest(env: AnyEnvelope | undefined): void {
    if (!env || env.id === this.lastEnvelopeId) return;
    this.lastEnvelopeId = env.id;
    if (this.muted) return;
    const cue = env.scene_hint?.audio_cue as AudioCue | undefined;
    if (!cue) return;
    // Rate-limit chatty cues like ``bubble_pop`` that fire on every
    // signal_emitted.
    const cooldown = COOLDOWN_MS_PER_CUE[cue] ?? 0;
    if (cooldown > 0) {
      const last = this.lastFiredAt.get(cue) ?? 0;
      if (Date.now() - last < cooldown) return;
      this.lastFiredAt.set(cue, Date.now());
    }
    this.play(cue);
  }

  /** Force a cue (used by tests + the optional debug helper). */
  play(cue: AudioCue): void {
    if (this.muted) return;
    const ctx = this.ensureContext();
    if (!ctx || !this.masterGain) return;
    if (isAmbientCue(cue)) {
      this.playAmbient(ctx, this.masterGain, cue);
      return;
    }
    synthOneShot(ctx, this.masterGain, cue);
  }

  /** Cleanly destroy the audio graph (component unmount). */
  destroy(): void {
    this.stopAmbient();
    try {
      this.masterGain?.disconnect();
      this.ctx?.close();
    } catch {
      /* noop */
    }
    this.ctx = null;
    this.masterGain = null;
  }

  private ensureContext(): AudioContext | null {
    if (this.ctx) return this.ctx;
    try {
      const Ctx =
        (window as unknown as { AudioContext?: typeof AudioContext })
          .AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext })
          .webkitAudioContext;
      if (!Ctx) return null;
      this.ctx = new Ctx();
      this.masterGain = this.ctx.createGain();
      this.masterGain.gain.value = 0.18; // keep cues comfortably quiet
      this.masterGain.connect(this.ctx.destination);
      return this.ctx;
    } catch {
      return null;
    }
  }

  private playAmbient(
    ctx: AudioContext,
    master: GainNode,
    cue: AudioCue,
  ): void {
    if (this.ambient?.kind === cue) return; // already running
    this.stopAmbient();
    const { freq, type } = AMBIENT_TONES[cue] ?? {
      freq: 220,
      type: 'sine' as OscillatorType,
    };
    const osc = ctx.createOscillator();
    osc.type = type;
    osc.frequency.value = freq;
    const gain = ctx.createGain();
    gain.gain.value = 0;
    gain.gain.linearRampToValueAtTime(0.04, ctx.currentTime + 0.6);
    osc.connect(gain);
    gain.connect(master);
    osc.start();
    this.ambient = { source: osc, gain, kind: cue };
  }

  private stopAmbient(): void {
    if (!this.ambient || !this.ctx) return;
    const { source, gain } = this.ambient;
    try {
      gain.gain.linearRampToValueAtTime(0, this.ctx.currentTime + 0.4);
      source.stop(this.ctx.currentTime + 0.45);
    } catch {
      /* noop */
    }
    this.ambient = null;
  }
}

function isAmbientCue(cue: AudioCue): boolean {
  return cue.startsWith('weather_') || cue === 'its_friday_baby';
}

const AMBIENT_TONES: Partial<Record<AudioCue, { freq: number; type: OscillatorType }>> = {
  weather_sunny_ambient: { freq: 440, type: 'sine' },
  weather_cloudy_ambient: { freq: 330, type: 'sine' },
  weather_storm_ambient: { freq: 110, type: 'sawtooth' },
  weather_windy_ambient: { freq: 260, type: 'triangle' },
  weather_snow_ambient: { freq: 520, type: 'sine' },
  weather_fog_ambient: { freq: 180, type: 'sine' },
  its_friday_baby: { freq: 392, type: 'triangle' }, // G4
};

/**
 * Play a single short percussive cue.  Each cue type has its own
 * pitch envelope so the operator can tell them apart by ear.
 */
function synthOneShot(ctx: AudioContext, master: GainNode, cue: AudioCue): void {
  const t0 = ctx.currentTime;
  switch (cue) {
    case 'bubble_pop':
      pluck(ctx, master, t0, [{ freq: 660, time: 0 }, { freq: 880, time: 0.06 }], 0.12);
      break;
    case 'thinking_chime':
      pluck(
        ctx,
        master,
        t0,
        [
          { freq: 880, time: 0 },
          { freq: 1320, time: 0.12 },
        ],
        0.25,
        'triangle',
      );
      break;
    case 'order_ping':
      pluck(ctx, master, t0, [{ freq: 1200, time: 0 }], 0.08);
      break;
    case 'order_fill_cha_ching':
      pluck(
        ctx,
        master,
        t0,
        [
          { freq: 1046, time: 0 }, // C6
          { freq: 1318, time: 0.08 }, // E6
          { freq: 1568, time: 0.16 }, // G6
        ],
        0.35,
      );
      break;
    case 'risk_warn_beep':
      pluck(
        ctx,
        master,
        t0,
        [
          { freq: 660, time: 0 },
          { freq: 440, time: 0.16 },
        ],
        0.3,
        'square',
      );
      break;
    case 'frozen_door_slam':
      pluck(ctx, master, t0, [{ freq: 80, time: 0 }], 0.5, 'sawtooth', 0.5);
      break;
    case 'unfreeze_thaw':
      pluck(
        ctx,
        master,
        t0,
        [
          { freq: 220, time: 0 },
          { freq: 440, time: 0.2 },
          { freq: 660, time: 0.4 },
        ],
        0.6,
        'sine',
      );
      break;
    case 'reduce_haircut_swoosh':
      pluck(
        ctx,
        master,
        t0,
        [
          { freq: 1320, time: 0 },
          { freq: 440, time: 0.3 },
        ],
        0.3,
        'triangle',
      );
      break;
    case 'utility_calc_tick':
      pluck(ctx, master, t0, [{ freq: 1760, time: 0 }], 0.06, 'square', 0.18);
      break;
    case 'leaderboard_climb':
      pluck(
        ctx,
        master,
        t0,
        [
          { freq: 523, time: 0 },
          { freq: 659, time: 0.1 },
          { freq: 784, time: 0.2 },
          { freq: 1046, time: 0.3 },
        ],
        0.45,
      );
      break;
    case 'confetti_burst':
      pluck(
        ctx,
        master,
        t0,
        [
          { freq: 1318, time: 0 },
          { freq: 1568, time: 0.08 },
          { freq: 2093, time: 0.16 },
          { freq: 1568, time: 0.24 },
        ],
        0.5,
        'square',
        0.32,
      );
      break;
    case 'applause':
      // Filtered white noise pulse.
      noisePulse(ctx, master, t0, 0.6);
      break;
    case 'morning_chime':
      pluck(
        ctx,
        master,
        t0,
        [
          { freq: 659, time: 0 }, // E5
          { freq: 784, time: 0.18 }, // G5
          { freq: 988, time: 0.36 }, // B5
        ],
        0.7,
        'sine',
      );
      break;
    case 'closing_bell':
      pluck(
        ctx,
        master,
        t0,
        [
          { freq: 392, time: 0 },
          { freq: 294, time: 0.3 },
          { freq: 220, time: 0.6 },
        ],
        1.0,
        'sine',
      );
      break;
    default:
      pluck(ctx, master, t0, [{ freq: 440, time: 0 }], 0.12);
  }
}

function pluck(
  ctx: AudioContext,
  master: GainNode,
  startAt: number,
  notes: Array<{ freq: number; time: number }>,
  durationSec: number,
  type: OscillatorType = 'sine',
  peakGain = 0.25,
): void {
  for (const note of notes) {
    const osc = ctx.createOscillator();
    osc.type = type;
    osc.frequency.value = note.freq;
    const gain = ctx.createGain();
    const start = startAt + note.time;
    const end = startAt + note.time + durationSec;
    gain.gain.setValueAtTime(0, start);
    gain.gain.linearRampToValueAtTime(peakGain, start + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.0001, end);
    osc.connect(gain);
    gain.connect(master);
    osc.start(start);
    osc.stop(end + 0.02);
  }
}

function noisePulse(
  ctx: AudioContext,
  master: GainNode,
  startAt: number,
  durationSec: number,
): void {
  const bufferSize = Math.floor(ctx.sampleRate * durationSec);
  const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < bufferSize; i++) {
    // Decaying white noise -> sounds like a crowd clap.
    const envelope = 1 - i / bufferSize;
    data[i] = (Math.random() * 2 - 1) * envelope * 0.6;
  }
  const src = ctx.createBufferSource();
  src.buffer = buffer;
  const gain = ctx.createGain();
  gain.gain.value = 0.35;
  src.connect(gain);
  gain.connect(master);
  src.start(startAt);
  src.stop(startAt + durationSec);
}
